import { ArrowLeftOutlined } from '@ant-design/icons';
import { Layout as AntLayout, Breadcrumb, Button, Descriptions, Dropdown, Menu, MenuProps, Modal, PageHeader, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { Dispatch } from 'redux';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import ILoan from '~Api/Loan/ILoan';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import {
    loanDrawdownApproveAction,
    loanDrawdownRejectAction,
    loanDrawdownSubmitForApprovalAction,
    loanGetAction,
    loanWarehouseLoansListAction,
} from '~Loans/actions';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import {
    loanDrawdownSelector,
    loanSelector,
    loanWarehouseLoansSelector,
} from '~/Loans/selectors';
import '~/LoanDrawdowns/LoanDrawdown/loan-drawdown.less';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import menuDivider from '~UI/menuDivider';
import { currencyFormatter } from '~utilities/formatters';
import { Helmet } from 'react-helmet';
import ModalStateEnum from '~UI/ModalStateEnum';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import LoanDrawdownStatusTag from '../LoanDrawdownStatusTag';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import DrawdownQsReceivedModal from './DrawdownQsReceivedModal';
import DrawdownProcessModal from './DrawdownProcessModal';
import { warehousesSelector } from '~Warehouses/selectors';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import IWarehouseLoan from '~Api/Warehouse/IWarehouseLoan';
import { warehousesListAction } from '~Warehouses/actions';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import DrawdownDisbursalInstructedModal from './DrawdownDisbursalInstructedModal';
import DrawdownQsInstructedModal from './DrawdownQsInstructedModal';
import _ from 'lodash';

export default function Layout(props: {
    loanUuid: string;
    breadcrumb?: JSX.Element;
    children: ReactElement | ReactElement[];
    loanDrawdownUuid: string;
    section: string;
}): ReactElement {
    const { breadcrumb, children, section, loanUuid, loanDrawdownUuid } = props;

    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);
    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));
    const loan: ILoan = useSelector((state: IGlobalState) => loanSelector(state, loanUuid));
    const warehouses: IDictionary<IWarehouse> = useSelector(warehousesSelector);
    const loanWarehouseLoans: IDictionary<IWarehouseLoan> = useSelector((state: IGlobalState) => loanWarehouseLoansSelector(state, loanUuid));

    const [ isDisbursalInstructedModalOpen, setIsDisbursalInstructedModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isQsInstructedModalOpen, setIsQsInstructedModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isQsReceivedModalOpen, setIsQsReceivedModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isProcessModalOpen, setIsProcessModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect(() => {
        if (!drawdown) {
            dispatch(loanGetAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        drawdown,
        loan,
        loanDrawdownUuid,
    ]);

    useEffect(() => {
        if (!warehouses && currentUser.permissions.includes(PermissionsEnum.Warehouses)) {
            dispatch(warehousesListAction());
        }
    }, [
        currentUser.permissions,
        dispatch,
        warehouses,
    ]);

    useEffect(() => {
        if (!loanWarehouseLoans && currentUser.permissions.includes(PermissionsEnum.Warehouses)) {
            dispatch(loanWarehouseLoansListAction(loanUuid));
        }
    }, [
        currentUser.permissions,
        dispatch,
        loanUuid,
        loanWarehouseLoans,
    ]);

    const onClickApprove: () => void = useCallback(() => {
        Modal.confirm({
            content: 'Are you sure you want to approve this drawdown?',
            okText: 'Approve',
            onOk: () => {
                dispatch(loanDrawdownApproveAction(loanDrawdownUuid));
            },
            title: 'Approve',
        });
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onClickReject: () => void = useCallback(() => {
        Modal.confirm({
            content: 'Are you sure you want to reject this drawdown?',
            okText: 'Reject',
            onOk: () => {
                dispatch(loanDrawdownRejectAction(loanDrawdownUuid));
            },
            title: 'Reject',
        });
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onClickInstructDisbursal: () => void = useCallback(() => {
        setIsDisbursalInstructedModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseDisbursalInstructedModal: () => void = useCallback(() => {
        setIsDisbursalInstructedModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickProcess: () => void = useCallback(() => {
        setIsProcessModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseProcessModal: () => void = useCallback(() => {
        setIsProcessModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickQsInstructed: () => void = useCallback(() => {
        setIsQsInstructedModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseQsInstructedModal: () => void = useCallback(() => {
        setIsQsInstructedModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickQsReceived: () => void = useCallback(() => {
        setIsQsReceivedModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseQsReceivedModal: () => void = useCallback(() => {
        setIsQsReceivedModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickSubmitForApproval: () => void = useCallback(() => {
        dispatch(loanDrawdownSubmitForApprovalAction(loanDrawdownUuid));
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    if (!drawdown || !loan) {
        return (
            <AntLayout className='loan-drawdown'>
                <Spin />
            </AntLayout>
        );
    }

    const actionsMenu: MenuProps = {
        items: [
            {
                danger: true,
                key: 'reject',
                label: 'Reject',
                onClick: onClickReject,
            },
        ],
    };

    const approveActionBlock: JSX.Element = currentUser.permissions.includes(PermissionsEnum.LoanDrawdownApprove) && drawdown.status === LoanDrawdownStatusEnum.PendingApproval && (
        <Dropdown.Button type='primary' onClick={onClickApprove} menu={actionsMenu}>Approve</Dropdown.Button>
    );

    const processActionBlock: ReactElement = currentUser.permissions.includes(PermissionsEnum.Accounts) && drawdown.status === LoanDrawdownStatusEnum.DisbursalInstructed && (
        <Button type='primary' onClick={onClickProcess}>Process</Button>
    );

    let loanHasFwt1Warehouse: boolean = false;

    if (warehouses && loanWarehouseLoans) {
        _.forEach(loanWarehouseLoans, (loanWarehouseLoan: IWarehouseLoan) => {
            if (WarehouseTypeEnum.Fwt1 === warehouses[loanWarehouseLoan.warehouseUuid].type && loanWarehouseLoan.balancePrincipal <= 0) {
                loanHasFwt1Warehouse = true;
            }
        });
    }

    const disbursalInstructedMenu: MenuProps = !!loanHasFwt1Warehouse && drawdown.status === LoanDrawdownStatusEnum.Approved && {
        items: [
            {
                key: 'download-offer-to-sell',
                label: <a href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-offer-to-sell`} target='_blank' rel='noreferrer'>Download Offer To Sell</a>,
            },
            {
                key: 'download-interest-calculation',
                label: <a href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-interest-calculation`} target='_blank' rel='noreferrer'>Download Interest Calculation</a>,
            },
        ],
    };

    // For FWT1, display a dropdown button with options to download offer to sell and interest calculation
    const disbursalInstructedButton: ReactElement = loanHasFwt1Warehouse && drawdown.status === LoanDrawdownStatusEnum.Approved ? (
        <Dropdown.Button
            onClick={onClickInstructDisbursal}
            menu={disbursalInstructedMenu}
            type='primary'
        >
            Disbursal Instructed
        </Dropdown.Button>
    ) : drawdown.status === LoanDrawdownStatusEnum.Approved && (
        <Button
            onClick={onClickInstructDisbursal}
            type='primary'
        >
            Disbursal Instructed
        </Button>
    );

    const downloadSummaryBlock: ReactElement = ([
        LoanDrawdownStatusEnum.PendingApproval,
        LoanDrawdownStatusEnum.Approved,
        LoanDrawdownStatusEnum.DisbursalInstructed,
    ].includes(drawdown.status) || (drawdown.status === LoanDrawdownStatusEnum.Processed && drawdown.loanFileUuid)) && (
        <Button
            href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-summary`}
            target='_blank'
        >
            Download Summary
        </Button>
    );

    const disbursalInstructedModalBlock: JSX.Element = ModalStateEnum.Off !== isDisbursalInstructedModalOpen && (
        <DrawdownDisbursalInstructedModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isDisbursalInstructedModalOpen}
            onCancel={onCloseDisbursalInstructedModal}
        />
    );

    const qsInstructedModalBlock: JSX.Element = ModalStateEnum.Off !== isQsInstructedModalOpen && (
        <DrawdownQsInstructedModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isQsInstructedModalOpen}
            onCancel={onCloseQsInstructedModal}
        />
    );

    const qsReceivedModalBlock: JSX.Element = ModalStateEnum.Off !== isQsReceivedModalOpen && (
        <DrawdownQsReceivedModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isQsReceivedModalOpen}
            onCancel={onCloseQsReceivedModal}
        />
    );

    const processModalBlock: ReactElement = ModalStateEnum.Off !== isProcessModalOpen && (
        <DrawdownProcessModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isProcessModalOpen}
            onCancel={onCloseProcessModal}
        />
    );

    const extra: JSX.Element = (
        <Space className='actions'>
            {drawdown.status === LoanDrawdownStatusEnum.Requested && <Button type='primary' onClick={onClickQsInstructed}>QS Instructed</Button>}
            {drawdown.status === LoanDrawdownStatusEnum.QsInstructed && <Button type='primary' onClick={onClickQsReceived}>QS Received</Button>}
            {downloadSummaryBlock}
            {drawdown.status === LoanDrawdownStatusEnum.QsReceived && <Button type='primary' onClick={onClickSubmitForApproval}>Submit for Approval</Button>}
            {approveActionBlock}
            {disbursalInstructedButton}
            {processActionBlock}
        </Space>
    );

    const menuItems: ItemType[] = [
        {
            key: 'overview',
            label: <NavLink to={`/loans/${loan.uuid}/drawdowns/${drawdown.uuid}`}>Overview</NavLink>,
        },
        menuDivider,
        {
            key: 'drawdowns',
            label: <NavLink to={`/loans/${loan.uuid}/drawdowns`}><ArrowLeftOutlined /> Drawdowns</NavLink>,
        },
        {
            key: 'loan',
            label: <NavLink to={`/loans/${loan.uuid}`}><ArrowLeftOutlined /> Loan</NavLink>,
        },
    ];

    return (
        <AntLayout className='loan-drawdown'>
            <Helmet>
                <title>{`Drawdown ${drawdown.drawdownNumber} - ${loan.code} - ${loan.name}`}</title>
            </Helmet>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                <Breadcrumb.Item><Link to={`/loans/${loan.uuid}`}>{loan.code}</Link></Breadcrumb.Item>
                <Breadcrumb.Item><Link to={`/loans/${loan.uuid}/drawdowns`}>Drawdowns</Link></Breadcrumb.Item>
                <Breadcrumb.Item>{drawdown.drawdownNumber}</Breadcrumb.Item>
            </Breadcrumb>
            <PageHeader
                className='header'
                extra={extra}
                ghost={false}
                subTitle={loan.code}
                tags={<LoanDrawdownStatusTag loanDrawdown={drawdown} />}
                title={`Drawdown ${drawdown.drawdownNumber}`}
            >
                <Descriptions size='small' column={3}>
                    <Descriptions.Item label='Requested Amount'>{currencyFormatter.format(drawdown.requestedAmount)}</Descriptions.Item>
                    <Descriptions.Item label='Date Requested'>{dayjs(drawdown.requestedTime).format('Do MMMM YYYY')}</Descriptions.Item>
                    <Descriptions.Item label='Approved Amount'>{drawdown.approvedDrawdownAmount ? currencyFormatter.format(drawdown.approvedDrawdownAmount) : '-'}</Descriptions.Item>
                    <Descriptions.Item label='Date Processed'>{drawdown.processedTime ? dayjs(drawdown.processedTime).format('Do MMMM YYYY') : '-'}</Descriptions.Item>
                </Descriptions>
            </PageHeader>
            <AntLayout className='content-wrapper'>
                <AntLayout.Sider className='menu' theme='light' width={240}>
                    <Menu
                        defaultSelectedKeys={[ section ]}
                        items={menuItems}
                        mode='inline'
                    />
                </AntLayout.Sider>
                <AntLayout.Content className={`content ${section}`}>
                    {children}
                </AntLayout.Content>
            </AntLayout>
            {disbursalInstructedModalBlock}
            {qsInstructedModalBlock}
            {qsReceivedModalBlock}
            {processModalBlock}
        </AntLayout>
    );
}